import { useContext } from 'react';
import { LegendItem, LegendLabel, LegendOrdinal, LegendShape } from '@visx/legend';
import { FormattedLabel } from '@visx/legend/lib/types';
import { DatumObject } from '@visx/shape/lib/types';
import { DataContext } from '@visx/xychart';

import { FlexBox } from '../../flex-box';
import { Text } from '../../text';
import { ChartContextProps } from '../charts.model';

interface LegendProps<T> extends Pick<ChartContextProps, 'formatLabel'> {
    isDisabled: (key: keyof T) => boolean;
    onKeyToggle: (key: keyof T) => void;
}

const DISABLED = 'var(--color-surface-interact)';

export const Legend = <T extends DatumObject>({ isDisabled, formatLabel, onKeyToggle }: LegendProps<T>) => {
    const { colorScale } = useContext(DataContext);

    const getColor = (label: FormattedLabel<string, string>) => {
        if (isDisabled(label.text)) return DISABLED;
        return colorScale?.(label.text);
    };

    if (!colorScale) return null;

    return (
        <div className="absolute w-[calc(100%-3rem)] border-t border-surface-divider pt-2 gap-4 flex left-1/2 -translate-x-1/2 -translate-y-6">
            <LegendOrdinal scale={colorScale}>
                {labels => (
                    <FlexBox gap="md" overflowX="auto">
                        {labels.map((label, i) => (
                            <button key={`${label.text}-${label.index}`} onClick={() => onKeyToggle(label.text)}>
                                <LegendItem
                                    shape="circle"
                                    key={`legend-quantile-${label.text}`}
                                    className="gap-.5 flex"
                                >
                                    <LegendShape
                                        fill={getColor}
                                        itemIndex={i}
                                        label={label}
                                        height={8}
                                        width={8}
                                        item={label.text}
                                        shape="circle"
                                    />
                                    <LegendLabel align="left">
                                        <div className="w-max">
                                            <Text ml={2} size="xs">
                                                {formatLabel(label.text)}
                                            </Text>
                                        </div>
                                    </LegendLabel>
                                </LegendItem>
                            </button>
                        ))}
                    </FlexBox>
                )}
            </LegendOrdinal>
        </div>
    );
};
